/**
 * @file forms.jsx
 * @location src/components/ui/sharedui/forms.jsx
 * @role 폼/섹션 헤더 원자 — Field / SectionTitle (모두 memo)
 */
import { memo, useId, cloneElement, isValidElement } from 'react';

const NATIVE_FORM_TAGS = new Set(['input', 'textarea', 'select']);

/**
 * flabel + 입력 자식 묶음 (모달 폼)
 *
 * children 이 네이티브 input/textarea/select 면 자동으로 id 주입 + label htmlFor 연결
 * (라벨 클릭 → 입력 포커스 + 스크린리더 announce). 커스텀 컴포넌트는 div 라벨 유지.
 *
 * @param {object} props
 * @param {string} props.label
 * @param {boolean} [props.required]
 * @param {React.ReactNode} props.children
 * @param {string} [props.hint]
 * @param {string} [props.error]   검증 실패 메시지 (라벨·hint 빨강 전환)
 */
export const Field = memo(function Field({ label, required, children, hint, error }) {
  const autoId = useId();
  const isNative = isValidElement(children)
    && typeof children.type === 'string'
    && NATIVE_FORM_TAGS.has(children.type);
  const inputId = isNative ? (children.props.id ?? autoId) : null;
  const child = (isNative && !children.props.id) ? cloneElement(children, { id: inputId }) : children;

  const labelInner = (
    <>
      {label}
      {required && <span className="req" aria-label="필수 항목">*필수</span>}
    </>
  );

  return (
    <div className="ffield">
      {isNative
        ? <label className={`flabel${error ? ' err' : ''}`} htmlFor={inputId}>{labelInner}</label>
        : <div className={`flabel${error ? ' err' : ''}`}>{labelInner}</div>}
      {child}
      {error
        ? <div className="fhint" style={{ color: 'var(--r)', fontWeight: 600 }}>{error}</div>
        : (hint && <div className="fhint">{hint}</div>)}
    </div>
  );
});

/** 섹션 헤더 — 아이콘 + 제목 + 카운트 + 부제 */
export const SectionTitle = memo(function SectionTitle({ icon, children, count, sub }) {
  return (
    <div className="sect-title">
      {icon && <span className="sect-title-icon" aria-hidden="true">{icon}</span>}
      <span className="sect-title-text">{children}</span>
      {count != null && <span className="sect-title-count">{count}</span>}
      {sub && <span className="sect-title-sub">{sub}</span>}
    </div>
  );
});
